import React, { Component } from 'react';
import { Shield, Download, Settings, Users, Database, FileText, Clock, CheckCircle, AlertCircle, Activity, ArrowRight, Plus, Trash2, RefreshCw, Mail, UserCheck, History } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import { AppButton } from '../components/base/AppButton';
// --- Mock Data & Types ---
type RequestStatus = 'Pending' | 'In Progress' | 'Completed';
type RequestType = 'Access' | 'Deletion' | 'Portability' | 'Rectification';
interface DataRequest {
  id: string;
  type: RequestType;
  user: string;
  email: string;
  status: RequestStatus;
  timestamp: string;
  description: string;
}
interface ConsentCategory {
  name: string;
  consentRate: number;
  totalUsers: number;
  trend: number;
}
interface RetentionPolicy {
  dataType: string;
  retentionDays: number;
  nextCleanup: string;
  itemsCount: number;
  progress: number;
}
interface ActivityItem {
  id: string;
  type: 'consent' | 'request' | 'policy' | 'system';
  description: string;
  user: string;
  timestamp: string;
}
const MOCK_REQUESTS: DataRequest[] = [{
  id: 'REQ-082',
  type: 'Deletion',
  user: 'Marco Rossi',
  email: 'm.rossi@example.com',
  status: 'Pending',
  timestamp: '2023-10-24T10:30:00Z',
  description: 'Richiesta di cancellazione totale dei dati personali e storico chat.'
}, {
  id: 'REQ-081',
  type: 'Access',
  user: 'Laura Bianchi',
  email: 'l.bianchi@example.com',
  status: 'In Progress',
  timestamp: '2023-10-23T15:45:00Z',
  description: 'Richiesta copia dei dati di profilazione e log di sistema.'
}, {
  id: 'REQ-080',
  type: 'Portability',
  user: 'Giuseppe Verdi',
  email: 'g.verdi@example.com',
  status: 'Completed',
  timestamp: '2023-10-21T09:15:00Z',
  description: 'Esportazione dati in formato JSON per trasferimento.'
}, {
  id: 'REQ-079',
  type: 'Rectification',
  user: 'Anna Neri',
  email: 'a.neri@example.com',
  status: 'Completed',
  timestamp: '2023-10-20T14:20:00Z',
  description: 'Aggiornamento preferenze di contatto e dati anagrafici.'
}];
const MOCK_CONSENTS: ConsentCategory[] = [{
  name: 'Marketing & Newsletter',
  consentRate: 68,
  totalUsers: 12450,
  trend: 2.4
}, {
  name: 'Analytics & Performance',
  consentRate: 85,
  totalUsers: 15500,
  trend: 0.5
}, {
  name: 'Third-party Sharing',
  consentRate: 42,
  totalUsers: 7680,
  trend: -1.2
}, {
  name: 'AI Profiling & Training',
  consentRate: 74,
  totalUsers: 13540,
  trend: 5.8
}];
const MOCK_RETENTION: RetentionPolicy[] = [{
  dataType: 'Chat History',
  retentionDays: 90,
  nextCleanup: 'Oggi',
  itemsCount: 45200,
  progress: 98
}, {
  dataType: 'System Logs',
  retentionDays: 30,
  nextCleanup: 'Domani',
  itemsCount: 128000,
  progress: 85
}, {
  dataType: 'User Analytics',
  retentionDays: 365,
  nextCleanup: 'Tra 5 giorni',
  itemsCount: 8500,
  progress: 45
}, {
  dataType: 'Inactive Accounts',
  retentionDays: 730,
  nextCleanup: 'Tra 12 giorni',
  itemsCount: 340,
  progress: 20
}];
const MOCK_ACTIVITIES: ActivityItem[] = [{
  id: 'act-1',
  type: 'request',
  description: 'Nuova richiesta di cancellazione dati',
  user: 'Marco Rossi',
  timestamp: '10 min fa'
}, {
  id: 'act-2',
  type: 'system',
  description: 'Esecuzione automatica data cleanup (Logs)',
  user: 'System',
  timestamp: '2 ore fa'
}, {
  id: 'act-3',
  type: 'consent',
  description: 'Aggiornamento massivo consensi AI Training',
  user: 'Admin',
  timestamp: 'Ieri, 15:30'
}, {
  id: 'act-4',
  type: 'policy',
  description: 'Modifica retention policy per Chat History',
  user: 'Admin',
  timestamp: 'Ieri, 11:15'
}, {
  id: 'act-5',
  type: 'request',
  description: 'Completata esportazione dati (Portability)',
  user: 'Giuseppe Verdi',
  timestamp: '21 Ott, 09:15'
}];
// --- Helper Components ---
const StatusBadge = ({
  status
}: {
  status: RequestStatus;
}) => {
  switch (status) {
    case 'Completed':
      return <AppBadge variant="green" data-id="element-2464">
          <CheckCircle className="w-3 h-3 mr-1" data-id="element-2465" /> Completata
        </AppBadge>;
    case 'In Progress':
      return <AppBadge variant="blue" data-id="element-2466">
          <RefreshCw className="w-3 h-3 mr-1" data-id="element-2467" /> In Lavorazione
        </AppBadge>;
    case 'Pending':
      return <AppBadge variant="amber" data-id="element-2468">
          <Clock className="w-3 h-3 mr-1" data-id="element-2469" /> In Attesa
        </AppBadge>;
    default:
      return <AppBadge variant="default" data-id="element-2470">{status}</AppBadge>;
  }
};
const TypeIcon = ({
  type
}: {
  type: RequestType;
}) => {
  switch (type) {
    case 'Deletion':
      return <Trash2 className="w-4 h-4 text-red-400" data-id="element-2471" />;
    case 'Access':
      return <FileText className="w-4 h-4 text-blue-400" data-id="element-2472" />;
    case 'Portability':
      return <Download className="w-4 h-4 text-purple-400" data-id="element-2473" />;
    case 'Rectification':
      return <UserCheck className="w-4 h-4 text-amber-400" data-id="element-2474" />;
  }
};
const ActivityIcon = ({
  type
}: {
  type: ActivityItem['type'];
}) => {
  switch (type) {
    case 'request':
      return <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center" data-id="element-2475">
          <Mail className="w-3 h-3 text-blue-400" data-id="element-2476" />
        </div>;
    case 'system':
      return <div className="w-6 h-6 rounded-full bg-gray-500/10 flex items-center justify-center" data-id="element-2477">
          <Settings className="w-3 h-3 text-gray-400" data-id="element-2478" />
        </div>;
    case 'consent':
      return <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center" data-id="element-2479">
          <Shield className="w-3 h-3 text-green-400" data-id="element-2480" />
        </div>;
    case 'policy':
      return <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center" data-id="element-2481">
          <FileText className="w-3 h-3 text-amber-400" data-id="element-2482" />
        </div>;
  }
};
// --- Main Page Component ---
export function GDPRPage() {
  return <div className="min-h-screen bg-[#121212] pb-12" data-id="element-2483">
      {/* Header Section */}
      <div className="pt-6 px-4 md:px-8 pb-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2484">
        <div data-id="element-2485">
          <h1 className="text-[20px] font-semibold text-white tracking-tight flex items-center gap-2" data-id="element-2486">
            <Shield className="w-5 h-5 text-[#8eff01]" data-id="element-2487" />
            GDPR Compliance
          </h1>
          <p className="text-[13px] text-white/40 mt-1" data-id="element-2488">
            Gestione privacy, consensi e protezione dati personali
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-2489">
          <AppButton variant="secondary" className="gap-2" data-id="element-2490">
            <Download className="w-4 h-4" data-id="element-2491" />
            Export Report
          </AppButton>
          <AppButton variant="secondary" className="gap-2" data-id="element-2492">
            <Settings className="w-4 h-4" data-id="element-2493" />
            Impostazioni
          </AppButton>
        </div>
      </div>

      <div className="px-4 md:px-8 space-y-6" data-id="element-2494">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2495">
          <AppCard className="p-5" data-id="element-2496">
            <div className="flex items-center gap-2 mb-2" data-id="element-2497">
              <Users className="w-4 h-4 text-white/40" data-id="element-2498" />
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2499">
                Richieste Totali
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2500">
              <p className="font-bold text-white text-[24px]" data-id="element-2501">1,248</p>
              <span className="text-[12px] text-[#8eff01]" data-id="element-2502">+12%</span>
            </div>
          </AppCard>

          <AppCard className="p-5 border-amber-500/20 bg-amber-500/5" data-id="element-2503">
            <div className="flex items-center gap-2 mb-2" data-id="element-2504">
              <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-2505" />
              <span className="text-[11px] text-amber-500/70 uppercase tracking-wider font-medium" data-id="element-2506">
                In Attesa
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2507">
              <p className="font-bold text-white text-[24px]" data-id="element-2508">12</p>
              <span className="text-[12px] text-amber-500" data-id="element-2509">Da gestire</span>
            </div>
          </AppCard>

          <AppCard className="p-5" data-id="element-2510">
            <div className="flex items-center gap-2 mb-2" data-id="element-2511">
              <CheckCircle className="w-4 h-4 text-[#8eff01]" data-id="element-2512" />
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2513">
                Consent Rate
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2514">
              <p className="font-bold text-white text-[24px]" data-id="element-2515">84%</p>
              <span className="text-[12px] text-[#8eff01]" data-id="element-2516">Media globale</span>
            </div>
          </AppCard>

          <AppCard className="p-5" data-id="element-2517">
            <div className="flex items-center gap-2 mb-2" data-id="element-2518">
              <Database className="w-4 h-4 text-white/40" data-id="element-2519" />
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2520">
                Retention Compliance
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2521">
              <p className="font-bold text-white text-[24px]" data-id="element-2522">99.9%</p>
              <span className="text-[12px] text-white/40" data-id="element-2523">SLA rispettato</span>
            </div>
          </AppCard>
        </div>

        {/* Quick Actions Section */}
        <div data-id="element-2524">
          <h2 className="text-[14px] font-semibold text-white mb-4" data-id="element-2525">
            Azioni Rapide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2526">
            <button className="group text-left bg-[#1a1a1a] border border-white/[0.08] hover:border-[#8eff01]/50 rounded-xl p-5 transition-all" data-id="element-2527">
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] group-hover:bg-[#8eff01]/10 flex items-center justify-center mb-4 transition-colors" data-id="element-2528">
                <Plus className="w-5 h-5 text-white/70 group-hover:text-[#8eff01] transition-colors" data-id="element-2529" />
              </div>
              <h3 className="text-[14px] font-medium text-white mb-1" data-id="element-2530">
                Nuova Richiesta Dati
              </h3>
              <p className="text-[12px] text-white/40 mb-4" data-id="element-2531">
                Registra manualmente una richiesta di accesso, cancellazione o
                portabilità.
              </p>
              <div className="flex items-center text-[12px] font-medium text-[#8eff01]" data-id="element-2532">
                Registra richiesta{' '}
                <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2533" />
              </div>
            </button>

            <button className="group text-left bg-[#1a1a1a] border border-white/[0.08] hover:border-[#8eff01]/50 rounded-xl p-5 transition-all" data-id="element-2534">
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] group-hover:bg-[#8eff01]/10 flex items-center justify-center mb-4 transition-colors" data-id="element-2535">
                <Users className="w-5 h-5 text-white/70 group-hover:text-[#8eff01] transition-colors" data-id="element-2536" />
              </div>
              <h3 className="text-[14px] font-medium text-white mb-1" data-id="element-2537">
                Gestisci Consensi
              </h3>
              <p className="text-[12px] text-white/40 mb-4" data-id="element-2538">
                Visualizza e modifica le categorie di consenso e le preferenze
                degli utenti.
              </p>
              <div className="flex items-center text-[12px] font-medium text-[#8eff01]" data-id="element-2539">
                Gestisci categorie{' '}
                <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2540" />
              </div>
            </button>

            <button className="group text-left bg-[#1a1a1a] border border-white/[0.08] hover:border-[#8eff01]/50 rounded-xl p-5 transition-all" data-id="element-2541">
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] group-hover:bg-[#8eff01]/10 flex items-center justify-center mb-4 transition-colors" data-id="element-2542">
                <Database className="w-5 h-5 text-white/70 group-hover:text-[#8eff01] transition-colors" data-id="element-2543" />
              </div>
              <h3 className="text-[14px] font-medium text-white mb-1" data-id="element-2544">
                Policy Retention
              </h3>
              <p className="text-[12px] text-white/40 mb-4" data-id="element-2545">
                Configura le regole di conservazione e cancellazione automatica
                dei dati.
              </p>
              <div className="flex items-center text-[12px] font-medium text-[#8eff01]" data-id="element-2546">
                Configura policy{' '}
                <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2547" />
              </div>
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2548">
          {/* Left Column (Span 2) */}
          <div className="lg:col-span-2 space-y-6" data-id="element-2549">
            {/* Data Subject Requests Dashboard */}
            <AppCard padding="none" className="overflow-hidden flex flex-col" data-id="element-2550">
              <div className="p-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-2551">
                <div data-id="element-2552">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2553">
                    Richieste Interessati (DSR)
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2554">
                    Ultime richieste di esercizio dei diritti privacy
                  </p>
                </div>
                <AppButton variant="secondary" className="text-[12px] py-1.5 px-3" data-id="element-2555">
                  Vedi tutte
                </AppButton>
              </div>

              <div className="p-5 bg-white/[0.01]" data-id="element-2556">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2557">
                  {MOCK_REQUESTS.map(req => <div key={req.id} className="bg-[#1a1a1a] border border-white/[0.08] rounded-xl p-4 hover:border-white/[0.15] transition-colors flex flex-col h-full" data-id="element-2558">
                      <div className="flex items-start justify-between mb-3" data-id="element-2559">
                        <div className="flex items-center gap-2" data-id="element-2560">
                          <div className="p-1.5 bg-white/[0.04] rounded-md" data-id="element-2561">
                            <TypeIcon type={req.type} data-id="element-2562" />
                          </div>
                          <div data-id="element-2563">
                            <span className="text-[13px] font-medium text-white block" data-id="element-2564">
                              {req.type}
                            </span>
                            <span className="text-[10px] text-white/40" data-id="element-2565">
                              {req.id}
                            </span>
                          </div>
                        </div>
                        <StatusBadge status={req.status} data-id="element-2566" />
                      </div>

                      <div className="mb-4 flex-1" data-id="element-2567">
                        <p className="text-[13px] text-white/80 font-medium" data-id="element-2568">
                          {req.user}
                        </p>
                        <p className="text-[11px] text-white/40 mb-2" data-id="element-2569">
                          {req.email}
                        </p>
                        <p className="text-[12px] text-white/60 line-clamp-2 leading-relaxed" data-id="element-2570">
                          {req.description}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-white/[0.06] mt-auto" data-id="element-2571">
                        <span className="text-[11px] text-white/30 flex items-center gap-1" data-id="element-2572">
                          <Clock className="w-3 h-3" data-id="element-2573" />
                          {new Date(req.timestamp).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                        </span>
                        <button className="text-[12px] font-medium text-white/70 hover:text-white transition-colors" data-id="element-2574">
                          Gestisci
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>
            </AppCard>

            {/* Consent Management Overview */}
            <AppCard data-id="element-2575">
              <div className="flex items-center justify-between mb-6" data-id="element-2576">
                <div data-id="element-2577">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2578">
                    Panoramica Consensi
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2579">
                    Tassi di accettazione per categoria
                  </p>
                </div>
              </div>

              <div className="space-y-5" data-id="element-2580">
                {MOCK_CONSENTS.map((consent, idx) => <div key={idx} data-id="element-2581">
                    <div className="flex items-center justify-between mb-2" data-id="element-2582">
                      <div className="flex items-center gap-2" data-id="element-2583">
                        <span className="text-[13px] font-medium text-white/80" data-id="element-2584">
                          {consent.name}
                        </span>
                        <span className="text-[10px] text-white/40 px-1.5 py-0.5 bg-white/[0.04] rounded" data-id="element-2585">
                          {consent.totalUsers.toLocaleString()} utenti
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-2586">
                        <span className={`text-[11px] ${consent.trend > 0 ? 'text-[#8eff01]' : 'text-red-400'}`} data-id="element-2587">
                          {consent.trend > 0 ? '+' : ''}
                          {consent.trend}%
                        </span>
                        <span className="text-[13px] font-bold text-white" data-id="element-2588">
                          {consent.consentRate}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 w-full bg-white/[0.04] rounded-full overflow-hidden" data-id="element-2589">
                      <div className="h-full bg-[#8eff01] rounded-full transition-all duration-500" style={{
                    width: `${consent.consentRate}%`
                  }} data-id="element-2590" />
                    </div>
                  </div>)}
              </div>
            </AppCard>
          </div>

          {/* Right Column (Span 1) */}
          <div className="space-y-6" data-id="element-2591">
            {/* Data Retention Status */}
            <AppCard data-id="element-2592">
              <div className="flex items-center justify-between mb-6" data-id="element-2593">
                <div data-id="element-2594">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2595">
                    Data Retention
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2596">
                    Stato policy di conservazione
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-2597">
                {MOCK_RETENTION.map((policy, idx) => <div key={idx} className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg" data-id="element-2598">
                    <div className="flex items-center justify-between mb-2" data-id="element-2599">
                      <span className="text-[13px] font-medium text-white" data-id="element-2600">
                        {policy.dataType}
                      </span>
                      <span className="text-[11px] text-white/40" data-id="element-2601">
                        {policy.retentionDays} gg
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-3" data-id="element-2602">
                      <span className="text-[11px] text-white/60" data-id="element-2603">
                        {policy.itemsCount.toLocaleString()} record
                      </span>
                      <span className={`text-[11px] font-medium ${policy.nextCleanup === 'Oggi' ? 'text-amber-400' : 'text-[#8eff01]'}`} data-id="element-2604">
                        Cleanup: {policy.nextCleanup}
                      </span>
                    </div>

                    <div className="h-1.5 w-full bg-white/[0.04] rounded-full overflow-hidden" data-id="element-2605">
                      <div className={`h-full rounded-full transition-all duration-500 ${policy.progress > 90 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{
                    width: `${policy.progress}%`
                  }} data-id="element-2606" />
                    </div>
                  </div>)}
              </div>
            </AppCard>

            {/* Recent Activity Feed */}
            <AppCard data-id="element-2607">
              <div className="flex items-center justify-between mb-6" data-id="element-2608">
                <div data-id="element-2609">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2610">
                    Attività Recenti
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2611">
                    Log eventi privacy e GDPR
                  </p>
                </div>
                <History className="w-4 h-4 text-white/30" data-id="element-2612" />
              </div>

              <div className="relative pl-3 border-l border-white/[0.08] space-y-6 ml-2" data-id="element-2613">
                {MOCK_ACTIVITIES.map(activity => <div key={activity.id} className="relative" data-id="element-2614">
                    <div className="absolute -left-[25px] top-0 bg-[#1a1a1a] py-1" data-id="element-2615">
                      <ActivityIcon type={activity.type} data-id="element-2616" />
                    </div>
                    <div className="pl-2" data-id="element-2617">
                      <p className="text-[13px] text-white/80 leading-snug mb-1" data-id="element-2618">
                        {activity.description}
                      </p>
                      <div className="flex items-center gap-2 text-[11px]" data-id="element-2619">
                        <span className="text-white/60 font-medium" data-id="element-2620">
                          {activity.user}
                        </span>
                        <span className="text-white/20" data-id="element-2621">•</span>
                        <span className="text-white/40" data-id="element-2622">
                          {activity.timestamp}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>

              <button className="w-full mt-6 py-2 text-[12px] font-medium text-white/40 hover:text-white transition-colors border-t border-white/[0.06]" data-id="element-2623">
                Vedi tutto lo storico
              </button>
            </AppCard>
          </div>
        </div>
      </div>
    </div>;
}